
import React, { useState } from 'react';

const ApplicationForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic Info
    fullName: '',
    age: '',
    location: '',
    occupation: '',
    instagram: '',
    
    // Opening Statement
    openingStatement: '',
    
    // Deep Dive
    careerAchievement: '',
    travelDestination: '',
    idealSunday: '',
    familyRelationship: '',
    californiaConnections: '',
    convinceLexi: '',
    
    // Final Pitch
    finalPitch: ''
  });

  const totalSteps = 4;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = ['fullName', 'age', 'location', 'occupation', 'openingStatement'];
    const missingFields = requiredFields.filter(field => !formData[field as keyof typeof formData]);
    
    if (missingFields.length > 0) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      const submitData = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d46m4q7jsic9gsa4u74g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData
      });

      if (response.ok) {
        alert('Application submitted successfully! Lexi will review your application personally.');
        // Reset form
        setFormData({
          fullName: '', age: '', location: '', occupation: '', instagram: '',
          openingStatement: '', careerAchievement: '', travelDestination: '',
          idealSunday: '', familyRelationship: '', californiaConnections: '',
          convinceLexi: '', finalPitch: ''
        });
        setCurrentStep(1);
      } else {
        alert('There was an error submitting your application. Please try again.');
      }
    } catch (error) {
      alert('There was an error submitting your application. Please try again.');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">The Basics</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Age *
                </label>
                <input
                  type="number"
                  name="age"
                  min="28"
                  max="38"
                  value={formData.age}
                  onChange={(e) => handleInputChange('age', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Current Location *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                placeholder="City, State/Country"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Occupation *
              </label>
              <input
                type="text"
                name="occupation"
                value={formData.occupation}
                onChange={(e) => handleInputChange('occupation', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                placeholder="Your current role and company"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Instagram Handle
              </label>
              <input
                type="text"
                name="instagram"
                value={formData.instagram}
                onChange={(e) => handleInputChange('instagram', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                placeholder="@yourusername"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Your Opening Statement</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                In 100 words, why should Lexi go on a date with YOU? *
              </label>
              <textarea
                name="openingStatement"
                value={formData.openingStatement}
                onChange={(e) => handleInputChange('openingStatement', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                rows={6}
                maxLength={500}
                placeholder="Make it count. This is your elevator pitch."
                required
              />
              <div className="text-right text-sm text-slate-500 mt-1">
                {formData.openingStatement.length}/500 characters
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">The Deep Dive</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                What's the most impressive thing you've built in your career?
              </label>
              <textarea
                name="careerAchievement"
                value={formData.careerAchievement}
                onChange={(e) => handleInputChange('careerAchievement', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                rows={4}
                maxLength={500}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Where in the world should Lexi go next, and why with you?
              </label>
              <textarea
                name="travelDestination"
                value={formData.travelDestination}
                onChange={(e) => handleInputChange('travelDestination', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                rows={4}
                maxLength={500}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Describe your ideal Sunday - be specific.
              </label>
              <textarea
                name="idealSunday"
                value={formData.idealSunday}
                onChange={(e) => handleInputChange('idealSunday', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                rows={4}
                maxLength={500}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Where's home for you, and what's your relationship with your family?
              </label>
              <textarea
                name="familyRelationship"
                value={formData.familyRelationship}
                onChange={(e) => handleInputChange('familyRelationship', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                rows={4}
                maxLength={500}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Any Northern California connections? Her family wants to know.
              </label>
              <textarea
                name="californiaConnections"
                value={formData.californiaConnections}
                onChange={(e) => handleInputChange('californiaConnections', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                rows={3}
                maxLength={500}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Lexi's calendar is back-to-back meetings. How would you convince her to clear an evening for you?
              </label>
              <textarea
                name="convinceLexi"
                value={formData.convinceLexi}
                onChange={(e) => handleInputChange('convinceLexi', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                rows={4}
                maxLength={500}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Final Pitch</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                One last thing Lexi should know about you
              </label>
              <textarea
                name="finalPitch"
                value={formData.finalPitch}
                onChange={(e) => handleInputChange('finalPitch', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-sm"
                rows={6}
                maxLength={500}
                placeholder="Your closing argument. Make it memorable."
              />
              <div className="text-right text-sm text-slate-500 mt-1">
                {formData.finalPitch.length}/500 characters
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-6">
              <h4 className="font-semibold text-slate-900 mb-3">Photo Upload</h4>
              <p className="text-sm text-slate-600 mb-4">
                Upload 2-4 photos that show your personality and lifestyle. Quality over quantity.
              </p>
              <div className="text-center py-8 border-2 border-dashed border-slate-300 rounded-lg">
                <i className="ri-image-line text-4xl text-slate-400 mb-4"></i>
                <p className="text-slate-500">Photo uploads: Uncollectable</p>
                <p className="text-xs text-slate-400 mt-2">This feature is for display purposes</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section id="application" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Tell Lexi About Yourself
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Lexi reviews every application personally. Quality over quantity.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-700">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm text-slate-500">
              {Math.round((currentStep / totalSteps) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className="bg-slate-900 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} data-readdy-form className="bg-slate-50 rounded-2xl p-8 lg:p-12">
          {renderStep()}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-12 pt-8 border-t border-slate-200">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap ${
                currentStep === 1
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-slate-300 text-slate-700 hover:bg-slate-400'
              }`}
            >
              Previous
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                className="bg-slate-900 text-white px-8 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
              >
                Next Step
              </button>
            ) : (
              <button
                type="submit"
                className="bg-slate-900 text-white px-8 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
              >
                Get Geraghty
              </button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default ApplicationForm;
